import React, { useRef } from 'react';
import { Pie } from 'react-chartjs-2';
import { ChartOptions } from 'chart.js';
import { ChartControls, LoadingSpinner } from '../ui';
import { useTheme } from '../../contexts/ThemeContext';
import { getChartJsOptions, getChartColors, mergeChartOptions } from '../../utils/themeHelpers';
import { formatNumber } from '../../utils/formatters';
import useChartGuide from '../../hooks/useChartGuide';
import { useFavoriteToggle } from '../../hooks/useFavoriteToggle';
import { CHART_GUIDES } from '../../constants/chartGuides';
import { useQuery } from '@tanstack/react-query';
import { refineCustomerApi } from '../../services/apiService';

interface SurveyResponsesByAnswerProps {
  branchId?: number;
  showLoading?: boolean;
  onError?: (error: Error) => void;
}

const SurveyResponsesByAnswer: React.FC<SurveyResponsesByAnswerProps> = ({ 
  branchId = 0,
  showLoading = true,
  onError 
}) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const { theme: currentTheme } = useTheme();
  const { handleFavoriteToggle, isFavorited } = useFavoriteToggle();
  const { openGuide } = useChartGuide();
  
  // Component metadata
  const componentId = 'survey-responses-by-answer';
  const componentTitle = 'تعداد سوالات پاسخ داده شده به تفکیک نظر';

  // Fetch data using React Query with centralized API
  const { data: rawData = [], isLoading, error } = useQuery<Array<{ AnswerText: string; count: number }>>({
    queryKey: ['refine-customer-survey-responses-by-answer', branchId],
    queryFn: async () => {
      console.log('Fetching survey responses by answer for branch:', branchId);
      try {
        const data = await refineCustomerApi.getSurveyResponsesByAnswer(branchId);
        console.log('API Response data:', data);
        
        // The api.get already returns response.data, so we get the data directly
        if (Array.isArray(data)) {
          console.log('Data is array with length:', data.length);
          return data;
        }
        
        console.log('Data is not an array:', typeof data, data);
        return [];
      } catch (error) {
        console.error('Error fetching survey responses:', error);
        throw error;
      }
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: 2,
    enabled: !!branchId,
  });

  // Handle error
  React.useEffect(() => {
    if (error && onError) {
      onError(error as Error);
    }
  }, [error, onError]);

  // Debug logging
  React.useEffect(() => {
    console.log('SurveyResponsesByAnswer - branchId:', branchId);
    console.log('SurveyResponsesByAnswer - isLoading:', isLoading);
    console.log('SurveyResponsesByAnswer - error:', error);
    console.log('SurveyResponsesByAnswer - rawData:', rawData);
  }, [branchId, isLoading, error, rawData]);

  if (isLoading && showLoading) {
    return (
      <LoadingSpinner
        variant="minimal"
        size="lg"
        message="در حال بارگذاری…"
      />
    );
  }

  if (error) {
    return (
      <div className="card-base card-padding animate-enter animate-delay-3">
        <h3 className="title-card mb-4">{componentTitle}</h3>
        <div className="flex items-center justify-center h-64">
          <p className="text-muted">خطا در دریافت داده‌ها</p>
        </div>
      </div>
    );
  }

  if (!rawData || !rawData.length) {
    return (
      <div className="card-base card-padding animate-enter animate-delay-3">
        <h3 className="title-card mb-4">{componentTitle}</h3>
        <div className="flex items-center justify-center h-64">
          <p className="text-muted">داده‌ای برای نمایش وجود ندارد</p>
        </div>
      </div>
    );
  }

  // Process data and limit to top 5 items
  const data = rawData
    .map((item: any) => ({
      answer_text: item.AnswerText || 'نامشخص',
      count: item.count || 0
    }))
    .sort((a: any, b: any) => b.count - a.count)
    .slice(0, 5);

  // Chart colors based on theme
  const chartColors = getChartColors(currentTheme);

  // Prepare chart data
  const chartData = {
    labels: data.map((item: any) => item.answer_text),
    datasets: [{
      data: data.map((item: any) => item.count),
      backgroundColor: chartColors.slice(0, data.length),
      borderColor: chartColors.slice(0, data.length),
      borderWidth: 1
    }]
  };

  // Pie chart options using theme helpers
  const pieChartOptions: ChartOptions<'pie'> = mergeChartOptions(getChartJsOptions(currentTheme), {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        display: false
      },
      y: {
        display: false
      }
    },
    plugins: {
      legend: {
        // ...getChartJsOptions(currentTheme).plugins?.legend,
        // position: 'bottom',
        // // labels: {
        // //   padding: 15,
        // //   font: {
        // //     size: 12
        // //   }
        // // }
      },
      tooltip: {
        callbacks: {
          label: function (context: any) {
            const label = context.label || '';
            const value = context.parsed || 0;
            const total = context.dataset.data.reduce((a: number, b: number) => a + b, 0);
            const percentage = ((value / total) * 100).toFixed(1);
            return `${label}: ${formatNumber(value)} (${percentage}%)`;
          }
        }
      }
    }
  });

  return (
    <div ref={chartRef} className="card-base card-padding animate-enter animate-delay-3">
      <ChartControls
        componentId={componentId}
        componentTitle={componentTitle}
        componentType="chart"
        dashboardSource="refineCustomer"
        onInfo={() => openGuide(CHART_GUIDES['survey-responses-by-answer'])}
        onFavorite={() => handleFavoriteToggle({
          componentId,
          componentTitle,
          dashboardSource: 'refineCustomer',
          componentType: 'chart',
          description: 'نمودار دایره‌ای دلایل نارضایتی مشتریان',
          props: { branchId, showLoading, onError },
          gridLayout: {
            containerClass: 'card-base card-padding'
          }
        })}
        isFavorited={isFavorited(componentId)}
        chartRef={chartRef}
        className="mb-4"
      />
      <h3 className="title-card mb-4">{componentTitle}</h3>
      <div style={{ height: 300, width: '100%' }}>
        <Pie data={chartData} options={pieChartOptions} />
      </div>
      <div className="mt-4 text-center">
        <div className="inline-flex flex-wrap gap-3 justify-center">
          {data.map((item: any, index: number) => (
            <span 
              key={index} 
              className="px-3 py-1.5 rounded-full text-sm font-medium shadow-sm"
              style={{
                backgroundColor: `${chartColors[index]}20`,
                color: 'var(--content-primary, #1f2937)',
                border: `1.5px solid ${chartColors[index]}80`,
                boxShadow: `0 1px 3px ${chartColors[index]}20`
              }}
            >
              {item.answer_text}: {formatNumber(item.count)}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SurveyResponsesByAnswer;